import React, { useContext, useEffect } from 'react';

import type { IEditorQuery } from './../../ricos-content-query';
import {
  EditorContext,
  EditorQueryContext,
  ModalServiceContext,
  PluginsEventsContext,
} from './../../ricos-context';
import type { ModalService } from './../../ricos-modals';
import type { TiptapEditor } from './../../ricos-types';
import { ToolbarType } from './../../ricos-types';
import { AiPlusButton as AiPlusButtonComponent } from './../ai-plus-button/ai-plus-button';
import { UiComponentsProvider } from './../../ui-components';

import type { Prompt } from '../types';
import { AI_TYPE } from '../types';

type Position = { top: string };

type FloatingToolbarButtonProps = {
  toggleAiModal: (
    editor: TiptapEditor,
    modalsService: ModalService,
    initialPrompt?: Prompt
  ) => void;
  className: string;
  position: Position;
  toggleToolbarModal: (
    editor: TiptapEditor,
    modalsService: ModalService,
    refElement: HTMLDivElement
  ) => void;
  checkContextualMode: ({
    editorQuery,
    nodeId,
  }: {
    editorQuery: IEditorQuery['query'];
    nodeId?: string;
  }) => boolean;
};

export const FloatingToolbarButton: React.FC<FloatingToolbarButtonProps> = ({
  toggleAiModal,
  toggleToolbarModal,
  className,
  position,
  checkContextualMode,
}) => {
  const pluginsEvents = React.useContext(PluginsEventsContext);
  const [lockedPosition, setLockedPosition] = React.useState<Position | null>();
  const { adapter } = useContext(EditorContext);
  const modalService = useContext(ModalServiceContext);
  const editorQuery = useContext(EditorQueryContext);
  const [ref, setRef] = React.useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const resetPosition = () => {
      setLockedPosition(null);
    };
    adapter.tiptapEditor.on('selectionUpdate', resetPosition);

    return () => {
      adapter.tiptapEditor.off('selectionUpdate', resetPosition);
    };
  }, []);

  const openModal = React.useCallback(
    (initialPrompt?: Prompt) => {
      toggleAiModal(adapter.tiptapEditor, modalService, initialPrompt);
    },
    [adapter.tiptapEditor, modalService]
  );

  const selectBlock = () => {
    const { from, to } = adapter.tiptapEditor.state.selection;

    if (from === to) {
      adapter.tiptapEditor.commands.selectParentNode();
    }

    if (!lockedPosition) {
      setLockedPosition(position);
    }
  };

  return (
    <div className={className} style={lockedPosition || position} ref={setRef}>
      {ref && (
        <UiComponentsProvider containerEl={ref}>
          <AiPlusButtonComponent
            onClick={() => {
              pluginsEvents.publishPluginToolbarClick({
                pluginId: AI_TYPE,
                buttonName: 'plus',
                nodeId: '',
                type: ToolbarType.SIDE,
              });
              if (checkContextualMode({ editorQuery: editorQuery.query }) && ref) {
                selectBlock();
                toggleToolbarModal(adapter.tiptapEditor, modalService, ref);
              } else {
                openModal();
              }
            }}
          />
        </UiComponentsProvider>
      )}
    </div>
  );
};
